/**
 * Detect which import sources have a store on this machine, for the surfaces
 * that offer "bring your connections across" (start-page discovery card, New
 * Connection dialog). Lists sources on mount (cheap existsSync probes in the
 * main process); `withCounts` additionally scans each detected source so the
 * card can say how many connections are waiting. Fires
 * `feature:import_source_detected` at most once per source per app session.
 */
import { useEffect, useState } from 'react';
import type { ImportSourceId, ImportSourceInfo } from '../../../types/connectionImport';
import { countImportSources, listImportSources } from './connectionImportService';

// Module-level guard: fire each source's detection event at most once per
// session even if the empty state unmounts/remounts.
const trackedDetectedThisSession = new Set<string>();

/** Counts don't change while the app runs — scan each detected source once. */
let countsThisSession: Partial<Record<ImportSourceId, number>> | null = null;

export interface ImportDetectionOptions {
  /** Also count importable connections per detected source (one scan each). */
  withCounts?: boolean;
}

export function useImportDetection({ withCounts = false }: ImportDetectionOptions = {}) {
  const [sources, setSources] = useState<ImportSourceInfo[]>([]);
  const [counts, setCounts] = useState<Partial<Record<ImportSourceId, number>>>(
    () => countsThisSession ?? {}
  );
  // Distinguishes "counting hasn't answered yet" from "counting answered zero"
  // — the card shows on the first and hides on the second.
  const [countsLoaded, setCountsLoaded] = useState(() => countsThisSession !== null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const list = await listImportSources();
      if (cancelled) return;
      setSources(list);

      const detected = list.filter((s) => s.detected);
      for (const s of detected) {
        if (trackedDetectedThisSession.has(s.id)) continue;
        trackedDetectedThisSession.add(s.id);
        window.electronAPI
          ?.analytics?.track('feature:import_source_detected', { source: s.id })
          ?.catch(() => {});
      }

      if (!withCounts || detected.length === 0) return;
      if (countsThisSession) {
        setCounts(countsThisSession);
        setCountsLoaded(true);
        return;
      }
      const counted = await countImportSources(detected.map((s) => s.id));
      if (cancelled) return;
      // A count call that failed wholesale comes back empty: that is "unknown",
      // not "zero" — leave it uncached and unloaded so a later mount retries
      // rather than treating the failure as an answer.
      if (counted.length === 0) return;
      countsThisSession = Object.fromEntries(counted.map((c) => [c.sourceId, c.count]));
      setCounts(countsThisSession);
      setCountsLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [withCounts]);

  const detectedSources = sources.filter((s) => s.detected);
  const totalCount = detectedSources.reduce((sum, s) => sum + (counts[s.id] ?? 0), 0);

  return { detectedSources, counts, countsLoaded, totalCount };
}
